import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import AiAgentsAPI from '../../api/aiAgents';
import AiSkillsAPI from '../../api/aiSkills';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  currentAgent: null,
  skills: [],
  testResult: null,
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    isTestingConnection: false,
    isFetchingSkills: false,
  },
};

export const getters = {
  getAgents($state) {
    return $state.records;
  },
  getAgent: $state => id => {
    return $state.records.find(a => a.id === Number(id)) || null;
  },
  getCurrentAgent($state) {
    return $state.currentAgent;
  },
  getSkills($state) {
    return $state.skills;
  },
  getTestResult($state) {
    return $state.testResult;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
};

export const actions = {
  fetchAgents: async ({ commit }) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isFetching: true });
    try {
      const response = await AiAgentsAPI.get();
      commit(types.SET_AI_AGENTS, response.data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isFetching: false });
    }
  },

  fetchAgent: async ({ commit }, id) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isFetchingItem: true });
    try {
      const { data } = await AiAgentsAPI.show(id);
      commit(types.SET_AI_AGENT_CURRENT, data);
      commit(types.ADD_AI_AGENT, data);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isFetchingItem: false });
    }
  },

  createAgent: async ({ commit }, agentData) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isCreating: true });
    try {
      const response = await AiAgentsAPI.create(agentData);
      commit(types.ADD_AI_AGENT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isCreating: false });
    }
    return null;
  },

  updateAgent: async ({ commit }, { id, data }) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isUpdating: true });
    try {
      const response = await AiAgentsAPI.update(id, data);
      commit(types.EDIT_AI_AGENT, response.data);
      commit(types.SET_AI_AGENT_CURRENT, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isUpdating: false });
    }
    return null;
  },

  deleteAgent: async ({ commit }, id) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isDeleting: true });
    try {
      await AiAgentsAPI.delete(id);
      commit(types.DELETE_AI_AGENT, id);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isDeleting: false });
    }
  },

  testConnection: async ({ commit }, id) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isTestingConnection: true });
    commit(types.SET_AI_AGENT_TEST_RESULT, null);
    try {
      const { data } = await AiAgentsAPI.testConnection(id);
      commit(types.SET_AI_AGENT_TEST_RESULT, { ok: true, ...data });
      return data;
    } catch (error) {
      const message =
        error?.response?.data?.message || error?.message || 'Connection failed';
      commit(types.SET_AI_AGENT_TEST_RESULT, { ok: false, message });
      throw error;
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isTestingConnection: false });
    }
  },

  fetchSkills: async ({ commit }) => {
    commit(types.SET_AI_AGENT_UI_FLAG, { isFetchingSkills: true });
    try {
      const { data } = await AiSkillsAPI.get();
      commit(types.SET_AI_SKILLS, data);
    } catch (error) {
      // ignore
    } finally {
      commit(types.SET_AI_AGENT_UI_FLAG, { isFetchingSkills: false });
    }
  },

  clearCurrent: ({ commit }) => {
    commit(types.SET_AI_AGENT_CURRENT, null);
    commit(types.SET_AI_AGENT_TEST_RESULT, null);
  },
};

export const mutations = {
  [types.SET_AI_AGENT_UI_FLAG]($state, data) {
    $state.uiFlags = { ...$state.uiFlags, ...data };
  },
  [types.SET_AI_AGENTS]: MutationHelpers.set,
  [types.ADD_AI_AGENT]: MutationHelpers.setSingleRecord,
  [types.EDIT_AI_AGENT]: MutationHelpers.update,
  [types.DELETE_AI_AGENT]: MutationHelpers.destroy,
  [types.SET_AI_AGENT_CURRENT]($state, agent) {
    $state.currentAgent = agent;
  },
  [types.SET_AI_AGENT_TEST_RESULT]($state, result) {
    $state.testResult = result;
  },
  [types.SET_AI_SKILLS]($state, skills) {
    $state.skills = skills || [];
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
